import React, { useState, useRef, useEffect } from "react";
import { AuthModal, useAuth } from "./Auth";

const API = "https://api.assetgraph.ai";

const STARTERS = [
  { icon: "🔍", text: "Scan GitHub repo", prompt: "Scan https://github.com/Uniswap/v2-core and find vulnerabilities" },
  { icon: "🌐", text: "Check a domain", prompt: "Scan summer.fi and find attack paths" },
  { icon: "⚡", text: "Try DefiTuna", prompt: "Scan defituna.com and find attack paths" },
];

const STEP_LABELS = {
  scope: "Loading scope", supply_chain: "Scanning dependencies",
  osv: "Checking CVEs", secrets: "Scanning secrets",
  infra: "Infra recon", attack_paths: "Computing attack paths",
};

function VerdictBadge({ verdict }) {
  const colors = { PROVEN:"#FF4D6A",CRITICAL:"#FF4D6A",LIKELY:"#FFB547",HIGH:"#FFB547",POSSIBLE:"#4D9FEC",MEDIUM:"#4D9FEC",REFUTED:"#00E87A",LOW:"#8B95A8" };
  const c = colors[verdict] || "#8B95A8";
  return <span style={{ background:c+"20", color:c, border:"1px solid "+c+"60", borderRadius:4, padding:"2px 8px", fontSize:11, fontFamily:"monospace", fontWeight:700 }}>{verdict}</span>;
}

function FindingCard({ finding }) {
  const [open, setOpen] = useState(false);
  const verdict = finding.verdict || "POSSIBLE";
  const borders = { PROVEN:"#FF4D6A",CRITICAL:"#FF4D6A",LIKELY:"#FFB547",HIGH:"#FFB547",POSSIBLE:"#4D9FEC",REFUTED:"#00E87A" };
  const border = borders[verdict] || "#2A3347";
  return (
    <div onClick={() => setOpen(!open)} style={{ border:"1px solid "+border, borderRadius:8, marginBottom:8, cursor:"pointer", background:"#0E1118", overflow:"hidden" }}>
      <div style={{ display:"flex", alignItems:"center", gap:8, padding:"12px 16px" }}>
        <VerdictBadge verdict={verdict} />
        <span style={{ color:"#E8EBF0", fontSize:13, flex:1 }}>{finding.title || "Finding"}</span>
        {finding.blast_radius != null && <span style={{ color:"#525E72", fontSize:11, fontFamily:"monospace" }}>{Number(finding.blast_radius).toFixed(1)}/10</span>}
        <span style={{ color:"#525E72", fontSize:11 }}>{open ? "▲" : "▼"}</span>
      </div>
      {open && (
        <div style={{ borderTop:"1px solid #1E2535", padding:16 }}>
          {finding.description && <p style={{ color:"#8B95A8", fontSize:13, lineHeight:1.7, marginBottom:12 }}>{finding.description}</p>}
          {finding.asset && <div style={{ marginBottom:12 }}><div style={{ color:"#525E72", fontSize:10, fontFamily:"monospace", marginBottom:4 }}>ASSET</div><code style={{ color:"#4D9FEC", fontSize:12, background:"#141820", padding:"4px 10px", borderRadius:4 }}>{finding.asset}</code></div>}
          {finding.recommendation && (
            <div style={{ marginBottom:12 }}>
              <div style={{ color:"#525E72", fontSize:10, fontFamily:"monospace", marginBottom:4 }}>REMEDIATION</div>
              <div style={{ background:"#141820", borderLeft:"3px solid #00E87A", borderRadius:"0 6px 6px 0", padding:"10px 14px" }}>
                <p style={{ color:"#8B95A8", fontSize:13, lineHeight:1.7, margin:0 }}>{finding.recommendation}</p>
              </div>
            </div>
          )}
          {finding.rag_context && (
            <div>
              <div style={{ color:"#525E72", fontSize:10, fontFamily:"monospace", marginBottom:4 }}>KNOWLEDGE BASE</div>
              <div style={{ background:"#141820", borderLeft:"2px solid #2A3347", borderRadius:"0 6px 6px 0", padding:"10px 14px" }}>
                <p style={{ color:"#525E72", fontSize:11, lineHeight:1.6, margin:0 }}>{finding.rag_context.slice(0,400)}</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ExportBar({ findings, scanId }) {
  const doExport = async (fmt) => {
    try {
      const res = await fetch(API+"/export/"+fmt, { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({findings, scan_id:scanId||"scan"}) });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href=url; a.download="assetgraph."+fmt; a.click(); URL.revokeObjectURL(url);
    } catch(e) { alert("Export error: "+e.message); }
  };
  return (
    <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:10 }}>
      <span style={{ color:"#525E72", fontSize:11, fontFamily:"monospace" }}>EXPORT:</span>
      <button onClick={() => doExport("sarif")} style={{ background:"transparent", border:"1px solid #4D9FEC", borderRadius:6, padding:"3px 12px", color:"#4D9FEC", cursor:"pointer", fontSize:11, fontFamily:"monospace" }}>SARIF</button>
      <button onClick={() => doExport("json")} style={{ background:"transparent", border:"1px solid #4D9FEC", borderRadius:6, padding:"3px 12px", color:"#4D9FEC", cursor:"pointer", fontSize:11, fontFamily:"monospace" }}>JSON</button>
    </div>
  );
}

function ProgressSteps({ steps }) {
  const icons = { running:"⟳", done:"✓", error:"✗" };
  const colors = { running:"#FFB547", done:"#00E87A", error:"#FF4D6A" };
  return (
    <div style={{ background:"#0E1118", border:"1px solid #1E2535", borderRadius:8, padding:"12px 16px", marginTop:8 }}>
      {steps.map((s,i) => (
        <div key={i} style={{ display:"flex", alignItems:"center", gap:8, padding:"3px 0", fontFamily:"monospace", fontSize:12 }}>
          <span style={{ color:colors[s.status]||"#8B95A8", width:14 }}>{icons[s.status]||"○"}</span>
          <span style={{ color:"#8B95A8" }}>{STEP_LABELS[s.step]||s.step}</span>
          {s.detail && <span style={{ color:"#525E72" }}>— {s.detail}</span>}
        </div>
      ))}
    </div>
  );
}

function Message({ msg }) {
  const isUser = msg.role === "user";
  return (
    <div style={{ display:"flex", justifyContent:isUser?"flex-end":"flex-start", marginBottom:20, padding:"0 20px" }}>
      {!isUser && <div style={{ width:32, height:32, borderRadius:"50%", background:"#00E87A15", border:"1px solid #004D29", display:"flex", alignItems:"center", justifyContent:"center", color:"#00E87A", fontSize:11, fontWeight:700, marginRight:12, flexShrink:0, marginTop:2, fontFamily:"monospace" }}>AG</div>}
      <div style={{ maxWidth:"78%" }}>
        {msg.content && <div style={{ background:isUser?"#141820":"transparent", border:isUser?"1px solid #1E2535":"none", borderRadius:12, padding:isUser?"10px 16px":"2px 0", color:"#E8EBF0", fontSize:14, lineHeight:1.7, whiteSpace:"pre-wrap" }}>{msg.content}</div>}
        {msg.progress && msg.progress.length > 0 && <ProgressSteps steps={msg.progress} />}
        {msg.findings && msg.findings.length > 0 && (
          <div style={{ marginTop:12 }}>
            <div style={{ color:"#525E72", fontSize:11, fontFamily:"monospace", marginBottom:8, letterSpacing:"0.1em" }}>{msg.findings.length} FINDINGS — CLICK TO EXPAND</div>
            <ExportBar findings={msg.findings} scanId={msg.scanId} />
            {msg.findings.map((f,i) => <FindingCard key={i} finding={f} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function PaywallModal({ onClose }) {
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.85)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ background:"#0E1118", border:"1px solid #1E2535", borderRadius:16, padding:32, maxWidth:460, width:"90%" }}>
        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:20 }}>
          <div>
            <div style={{ color:"#00E87A", fontSize:10, fontFamily:"monospace", letterSpacing:"0.15em", marginBottom:6 }}>PRO FEATURE</div>
            <h2 style={{ color:"#fff", fontSize:20, fontWeight:700, margin:0 }}>Upgrade to Pro — $20/mo</h2>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", color:"#525E72", cursor:"pointer", fontSize:20 }}>x</button>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:24 }}>
          {[["Secrets scanning","EVM key derivation"],["Attack paths","Cross-layer chains"],["RAG context","1,232 incidents"],["SARIF export","CI/CD integration"]].map(([t,s]) => (
            <div key={t} style={{ background:"#141820", borderRadius:8, padding:"10px 14px" }}>
              <div style={{ color:"#E8EBF0", fontSize:13, fontWeight:600, marginBottom:2 }}>{t}</div>
              <div style={{ color:"#525E72", fontSize:11 }}>{s}</div>
            </div>
          ))}
        </div>
        <button onClick={async () => { try { const r=await fetch(API+"/billing/checkout",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({plan:"pro",cycle:"monthly"})}); const d=await r.json(); if(d.url) window.location.href=d.url; else alert(d.message||"Add Stripe keys to .env"); } catch(e){alert("Configure Stripe in backend/.env");} onClose(); }} style={{ width:"100%", background:"#00E87A", border:"none", borderRadius:10, padding:"12px", color:"#000", fontWeight:700, fontSize:14, cursor:"pointer" }}>
          Upgrade - $20/month
        </button>
        <div style={{ marginTop:12, textAlign:"center", color:"#525E72", fontSize:11 }}>14-day free trial · Cancel anytime</div>
      </div>
    </div>
  );
}

export default function App() {
  const [messages, setMessages] = useState([{ role:"assistant", content:"Ready. Paste a GitHub URL or domain to scan for attack paths." }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const { user, token, logout, setUser, setToken } = useAuth();
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior:"smooth" }); }, [messages]);

  const sendMessage = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages(prev => [...prev, { role:"user", content:msg }]);
    setLoading(true);
    const newMsg = { role:"assistant", content:"", progress:[], findings:[] };
    setMessages(prev => [...prev, newMsg]);
    try {
      const res = await fetch(API+"/chat/message", {
        method:"POST",
        headers:{ "Content-Type":"application/json", ...(token?{"Authorization":"Bearer "+token}:{}) },
        body:JSON.stringify({ message:msg, plan:user?user.plan:"free" }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while(true) {
        const {done,value} = await reader.read();
        if(done) break;
        buffer += decoder.decode(value,{stream:true});
        const lines = buffer.split("\n");
        buffer = lines.pop();
        for(const line of lines) {
          if(!line.startsWith("data: ")) continue;
          try {
            const event = JSON.parse(line.slice(6));
            setMessages(prev => {
              const msgs = [...prev];
              const last = {...msgs[msgs.length-1]};
              if(event.type==="token") last.content=(last.content||"")+event.content;
              else if(event.type==="progress") {
                const steps=[...(last.progress||[])];
                const idx=steps.findIndex(s=>s.step===event.step);
                if(idx>=0) steps[idx]=event; else steps.push(event);
                last.progress=steps;
              } else if(event.type==="findings") { last.findings=event.data||[]; }
              else if(event.type==="paywall") { setShowPaywall(true); }
              msgs[msgs.length-1]=last;
              return msgs;
            });
          } catch(e) {}
        }
      }
    } catch(e) {
      setMessages(prev => { const msgs=[...prev]; msgs[msgs.length-1].content="Error: "+e.message; return msgs; });
    }
    setLoading(false);
  };

  return (
    <>
      {showAuth && <AuthModal onClose={() => setShowAuth(false)} onSuccess={(u,t) => { setUser(u); setToken(t); setShowAuth(false); }} />}
      {showPaywall && <PaywallModal onClose={() => setShowPaywall(false)} />}
      <div style={{ display:"flex", height:"100vh", background:"#090B0F", color:"#E8EBF0", fontFamily:"Inter,system-ui,sans-serif" }}>
        <div style={{ width:240, background:"#0E1118", borderRight:"1px solid #1E2535", display:"flex", flexDirection:"column", padding:"16px 12px" }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:24, padding:"0 4px" }}>
            <div style={{ width:30, height:30, background:"#00E87A", borderRadius:7, display:"flex", alignItems:"center", justifyContent:"center", color:"#000", fontFamily:"monospace", fontSize:13, fontWeight:700 }}>AG</div>
            <span style={{ fontWeight:600, fontSize:15, letterSpacing:"-0.02em" }}>AssetGraph</span>
          </div>

          {user ? (
            <div style={{ marginBottom:16 }}>
              <div style={{ color:"#8B95A8", fontSize:11, marginBottom:6, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{user.email}</div>
              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
                <span style={{ fontSize:11, color:"#525E72", fontFamily:"monospace" }}>PLAN</span>
                <span style={{ fontSize:11, color:"#00E87A", fontFamily:"monospace", textTransform:"uppercase" }}>{user.plan||"free"}</span>
              </div>
              <button onClick={() => setShowPaywall(true)} style={{ width:"100%", background:"#00E87A15", border:"1px solid #004D29", borderRadius:8, padding:"7px", color:"#00E87A", cursor:"pointer", fontSize:12, fontWeight:600, marginBottom:8 }}>Upgrade - $20/mo</button>
              <button onClick={logout} style={{ width:"100%", background:"transparent", border:"1px solid #1E2535", borderRadius:8, padding:"6px", color:"#525E72", cursor:"pointer", fontSize:11 }}>Sign out</button>
            </div>
          ) : (
            <div style={{ marginBottom:16 }}>
              <button onClick={() => setShowAuth(true)} style={{ width:"100%", background:"#00E87A", border:"none", borderRadius:8, padding:"9px", color:"#000", cursor:"pointer", fontSize:13, fontWeight:700, marginBottom:8 }}>Sign in / Sign up</button>
              <div style={{ background:"#141820", borderRadius:6, padding:"8px 10px" }}>
                <div style={{ color:"#00E87A", fontSize:10, fontFamily:"monospace", marginBottom:3 }}>FREE TIER</div>
                <div style={{ color:"#525E72", fontSize:11 }}>3 free scans included</div>
              </div>
            </div>
          )}

          <button onClick={() => { setMessages([{role:"assistant",content:"Ready. Paste a GitHub URL or domain to scan."}]); setInput(""); }} style={{ background:"#141820", border:"1px solid #2A3347", borderRadius:8, padding:"9px 12px", color:"#E8EBF0", cursor:"pointer", fontSize:13, textAlign:"left", marginBottom:16 }}>+ New Scan</button>

          <div style={{ flex:1 }} />

          <div style={{ padding:"12px 8px", borderTop:"1px solid #1E2535" }}>
            <div style={{ fontSize:10, color:"#525E72", fontFamily:"monospace", letterSpacing:"0.1em", marginBottom:6 }}>RAG KNOWLEDGE BASE</div>
            {[["defi_incidents","1,232"],["playbooks","21"],["compliance","16"]].map(([k,v]) => (
              <div key={k} style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:"#525E72", fontFamily:"monospace", padding:"2px 0" }}>
                <span>{k}</span><span style={{ color:"#00E87A" }}>{v}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ flex:1, display:"flex", flexDirection:"column", minWidth:0 }}>
          <div style={{ flex:1, overflowY:"auto", paddingTop:28, paddingBottom:8 }}>
            {messages.map((msg,i) => <Message key={i} msg={msg} />)}
            {loading && messages[messages.length-1]?.content==="" && (
              <div style={{ padding:"0 20px 0 64px" }}>
                <span style={{ color:"#525E72", fontFamily:"monospace", fontSize:12 }}>thinking...</span>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {messages.length <= 1 && (
            <div style={{ display:"flex", gap:8, padding:"0 20px 12px", flexWrap:"wrap", justifyContent:"center" }}>
              {STARTERS.map((s,i) => (
                <button key={i} onClick={() => sendMessage(s.prompt)} style={{ background:"#0E1118", border:"1px solid #1E2535", borderRadius:8, padding:"8px 16px", color:"#8B95A8", cursor:"pointer", fontSize:13 }}>{s.icon} {s.text}</button>
              ))}
            </div>
          )}

          <div style={{ padding:"12px 20px 20px", borderTop:"1px solid #1E2535" }}>
            <div style={{ display:"flex", gap:10, alignItems:"flex-end", background:"#0E1118", border:"1px solid #2A3347", borderRadius:14, padding:"10px 14px" }}>
              <textarea value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();sendMessage();} }} placeholder="Paste a GitHub URL or domain to scan..." disabled={loading} rows={1} style={{ flex:1, background:"transparent", border:"none", color:"#E8EBF0", fontSize:14, resize:"none", outline:"none", fontFamily:"inherit", lineHeight:1.6, maxHeight:120, overflowY:"auto" }} />
              <button onClick={() => sendMessage()} disabled={loading||!input.trim()} style={{ background:input.trim()&&!loading?"#00E87A":"#1E2535", border:"none", borderRadius:10, padding:"7px 18px", color:input.trim()&&!loading?"#000":"#525E72", cursor:input.trim()&&!loading?"pointer":"default", fontSize:13, fontWeight:600, flexShrink:0 }}>Send</button>
            </div>
            <div style={{ textAlign:"center", marginTop:8, fontSize:11, color:"#3A4356" }}>Enter to send · Shift+Enter for new line</div>
          </div>
        </div>
      </div>
    </>
  );
}
